import {createElement, forwardRef} from "react";
import {Children} from "./Children";

type ElementProps = Omit<React.HTMLProps<HTMLElement>, "ref">;

type FlexViewProps = {
    /** FlexView content */
    children?: Children;
    /** flex-direction: column */
    column?: boolean;
    /** align content vertically */
    vAlignContent?: "top" | "center" | "bottom";
    /** align content horizontally */
    hAlignContent?: "left" | "center" | "right";
    /** margin-left property ("auto" to align self right) */
    marginLeft?: string | number;
    /** margin-top property ("auto" to align self bottom) */
    marginTop?: string | number;
    /** margin-right property ("auto" to align self left) */
    marginRight?: string | number;
    /** margin-bottom property ("auto" to align self top) */
    marginBottom?: string | number;
    /** grow property (for parent primary axis) */
    grow?: boolean | number;
    /** flex-shrink property */
    shrink?: boolean | number;
    /** flex-basis property */
    basis?: string | number;
    /** wrap content */
    wrap?: boolean;
    /** height property (for parent secondary axis) */
    height?: string | number;
    /** width property (for parent secondary axis) */
    width?: string | number;
    /** class to pass to top level element of the component */
    className?: string;
    /** style object to pass to top level element of the component */
    style?: React.CSSProperties;
    /** native dom component to render. Defaults to div */
    component?: keyof JSX.IntrinsicElements;
    componentRef?: React.Ref<HTMLElement>;
};

type Props = Pick<
    ElementProps,
    Exclude<keyof ElementProps, keyof FlexViewProps>
> &
    FlexViewProps;

const getGrow = ({grow}: FlexViewProps): number => {
    if (typeof grow === "number") {
        return grow;
    } else if (grow) {
        return 1;
    }

    return 0; // default
};

const getShrink = ({shrink, basis}: FlexViewProps): number => {
    if (typeof shrink === "number") {
        return shrink;
    } else if (shrink) {
        return 1;
    } else if (shrink === false) {
        return 0;
    }

    if (basis && basis !== "auto") {
        return 0;
    }

    return 1; // default
};

const getBasis = ({basis}: FlexViewProps): string => {
    if (basis) {
        const suffix =
            typeof basis === "number" ||
            String(parseInt(basis as string, 10)) === basis
                ? "px"
                : "";
        return basis + suffix;
    }

    return "auto"; // default
};

const getStyle = (props: FlexViewProps) => {
    const {column, wrap, vAlignContent, hAlignContent} = props;

    const style = {
        width: props.width,
        height: props.height,
        marginLeft: props.marginLeft,
        marginTop: props.marginTop,
        marginRight: props.marginRight,
        marginBottom: props.marginBottom,
    };

    const alignPropToFlex = (
        align: FlexViewProps["vAlignContent"] | FlexViewProps["hAlignContent"]
    ): string | undefined => {
        switch (align) {
            case "top":
            case "left":
                return "flex-start";
            case "center":
                return "center";
            case "bottom":
            case "right":
                return "flex-end";
            default:
                return;
        }
    };

    return {
        boxSizing: "border-box",

        // some browsers don't set these by default on flex
        minWidth: 0,
        minHeight: 0,

        // flex properties
        display: "flex",
        flexDirection: column ? "column" : "row",
        flexWrap: wrap ? "wrap" : "nowrap",
        flex: `${getGrow(props)} ${getShrink(props)} ${getBasis(props)}`,
        justifyContent: alignPropToFlex(column ? vAlignContent : hAlignContent),
        alignItems: alignPropToFlex(column ? hAlignContent : vAlignContent),

        // style passed through props
        ...style,
        ...props.style,
    };
};

const getElementProps = (
    props: FlexViewProps
): ElementProps & { [k in keyof FlexViewProps]?: never } => {
    const {
        children,
        className,
        style,
        column,
        grow,
        shrink,
        basis,
        wrap,
        vAlignContent,
        hAlignContent,
        width,
        height,
        marginBottom,
        marginTop,
        marginLeft,
        marginRight,
        componentRef,
        component,
        ...rest
    } = props;

    return rest;
};

const InnerBox = (props: Props) => {
    return createElement(props.component || "div", {
        ref: props.componentRef,
        className: props.className,
        style: getStyle(props),
        children: props.children,
        ...getElementProps(props),
    });
};

export const Box = forwardRef((props: Props, ref: React.Ref<HTMLElement>) => (
    <InnerBox shrink={props.shrink || false} componentRef={ref}
              {...props}
    />
));
